import React, { useState } from "react";
import { ChevronRight, Folder, FolderOpen, Plus, Pencil, MoveRight, Trash2, Library, Inbox } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const OPEN_KEY = "tr-kc-open";

export function FolderTree({ tree, selected, onSelect, onChanged, onDropFiles }) {
  const { items = [], total = 0, unfiled = 0, can_organize, can_organize_system } = tree;
  const [open, setOpen] = useState(() => { try { return JSON.parse(localStorage.getItem(OPEN_KEY)) || {}; } catch { return {}; } });
  const [dlg, setDlg] = useState(null);
  const toggle = (id) => { const n = { ...open, [id]: !open[id] }; setOpen(n); localStorage.setItem(OPEN_KEY, JSON.stringify(n)); };
  const kids = (pid) => items.filter((f) => (f.parent_id || null) === pid);
  const rolled = (f) => (f.count || 0) + kids(f.id).reduce((n, c) => n + rolled(c), 0);
  const row = (f, depth) => {
    const ch = kids(f.id);
    const isSel = selected === f.id;
    const needs = f.name === "Needs Filing" && !f.parent_id;
    const drop = tree.can_organize ? { onDragOver: (e) => e.preventDefault(), onDrop: (e) => { e.preventDefault(); const ids = JSON.parse(e.dataTransfer.getData("text/kc-ids") || "[]"); if (ids.length) onDropFiles(ids, f.id); } } : {};
    return (
      <div key={f.id}>
        <div {...drop} className={`group flex items-center gap-1 h-7 pr-1 rounded text-[13px] cursor-pointer ${isSel ? "bg-[#006090]/10 text-[#006090] font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
          style={{ paddingLeft: 4 + depth * 14 }} onClick={() => onSelect(f.id)} data-testid={`kc-folder-${f.id}`}>
          <button type="button" onClick={(e) => { e.stopPropagation(); toggle(f.id); }} className={`w-4 h-4 grid place-items-center ${ch.length ? "" : "invisible"}`} data-testid={`kc-folder-toggle-${f.id}`}>
            <ChevronRight className={`w-3 h-3 transition-transform ${open[f.id] ? "rotate-90" : ""}`} /></button>
          {isSel ? <FolderOpen className="w-3.5 h-3.5 shrink-0" /> : <Folder className="w-3.5 h-3.5 shrink-0 text-amber-500" />}
          <span className="truncate flex-1">{f.name}</span>
          {needs && f.count > 0 && <span className="text-[9px] rounded bg-amber-100 text-amber-800 px-1" data-testid="kc-needs-filing-badge">TO FILE</span>}
          {f.scope === "system" && <span className="text-[9px] rounded bg-indigo-50 text-indigo-600 px-1">SYS</span>}
          <span className="text-[11px] text-slate-400 tabular-nums group-hover:hidden">{rolled(f) || ""}</span>
          {f.can_organize && <span className="hidden group-hover:flex items-center gap-0.5">
            <Act icon={Plus} title="New subfolder" onClick={() => setDlg({ mode: "create", parent: f })} testId={`kc-folder-add-${f.id}`} />
            <Act icon={Pencil} title="Rename" onClick={() => setDlg({ mode: "rename", folder: f })} testId={`kc-folder-rename-${f.id}`} />
            <Act icon={MoveRight} title="Move" onClick={() => setDlg({ mode: "move", folder: f })} testId={`kc-folder-move-${f.id}`} />
            <Act icon={Trash2} title="Remove (empty folders only)" onClick={() => remove(f)} testId={`kc-folder-delete-${f.id}`} />
          </span>}
        </div>
        {open[f.id] && ch.map((c) => row(c, depth + 1))}
      </div>);
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove the empty folder "${f.name}"?`)) return;
    try { await api.delete(`/knowledge/folders/${f.id}`); toast.success("Folder removed"); onChanged(); if (selected === f.id) onSelect(""); } catch (e) { toast.error(apiError(e)); }
  };
  const fixed = (id, icon, label, n) => { const I = icon; return (
    <div onClick={() => onSelect(id)} data-testid={`kc-folder-${id || "all"}`}
      className={`flex items-center gap-1.5 h-7 px-2 rounded text-[13px] cursor-pointer ${selected === id ? "bg-[#006090]/10 text-[#006090] font-semibold" : "text-slate-700 hover:bg-slate-100"}`}>
      <I className="w-3.5 h-3.5" /><span className="flex-1">{label}</span><span className="text-[11px] text-slate-400 tabular-nums">{n}</span></div>); };
  return (
    <div className="text-sm" data-testid="kc-folder-tree">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Folders</span>
        {can_organize && <button type="button" onClick={() => setDlg({ mode: "create", parent: null })} className="text-[11px] text-[#006090] font-medium inline-flex items-center gap-0.5 hover:underline" data-testid="kc-folder-new">
          <Plus className="w-3 h-3" />New</button>}
      </div>
      {fixed("", Library, "Knowledge Center", total)}
      {kids(null).map((f) => row(f, 0))}
      {unfiled > 0 && fixed("none", Inbox, "No folder", unfiled)}
      {!can_organize && <p className="px-2 pt-2 text-[11px] text-slate-400" data-testid="kc-folder-readonly">Group Admins organize folders.</p>}
      {dlg && <FolderDialog dlg={dlg} items={items} canSystem={can_organize_system} onClose={() => setDlg(null)} onDone={() => { setDlg(null); onChanged(); }} />}
    </div>
  );
}

function Act({ icon: I, title, onClick, testId }) {
  return <button type="button" title={title} onClick={(e) => { e.stopPropagation(); onClick(); }} className="p-0.5 rounded hover:bg-white text-slate-500 hover:text-[#006090]" data-testid={testId}><I className="w-3 h-3" /></button>;
}

function FolderDialog({ dlg, items, canSystem, onClose, onDone }) {
  const [name, setName] = useState(dlg.folder?.name || "");
  const [parent, setParent] = useState(dlg.folder?.parent_id || "");
  const [scope, setScope] = useState("account");
  const [busy, setBusy] = useState(false);
  const f = dlg.folder;
  const descendants = (id) => items.filter((x) => x.parent_id === id).flatMap((x) => [x.id, ...descendants(x.id)]);
  const targets = f ? items.filter((x) => x.scope === f.scope && x.id !== f.id && !descendants(f.id).includes(x.id)) : [];
  const save = async () => {
    setBusy(true);
    try {
      if (dlg.mode === "create") await api.post("/knowledge/folders", { name, parent_id: dlg.parent?.id || null, scope });
      else if (dlg.mode === "rename") await api.patch(`/knowledge/folders/${f.id}`, { name });
      else await api.patch(`/knowledge/folders/${f.id}`, { parent_id: parent || null });
      toast.success("Folders updated"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const title = dlg.mode === "create" ? (dlg.parent ? `New subfolder in ${dlg.parent.name}` : "New folder") : dlg.mode === "rename" ? "Rename folder" : `Move "${f.name}"`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="kc-folder-dialog">
        <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
        {dlg.mode !== "move" ? <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Folder name" data-testid="kc-folder-name" />
          : <select value={parent} onChange={(e) => setParent(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="kc-folder-parent">
            <option value="">Top level</option>{targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}
        {dlg.mode === "create" && !dlg.parent && canSystem && (
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="kc-folder-scope">
            <option value="account">Organization folder</option><option value="system">System folder (all Organizations)</option></select>)}
        <DialogFooter><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={busy || (dlg.mode !== "move" && !name.trim())} onClick={save} data-testid="kc-folder-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
